import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ListboxSelect, ListboxMultiselect } from '@evertrue/et-components';
import Icon from 'components/elements/icon';
import DateFilterPopover from './date-filter-popover';
import NumericFilterPopover from './numeric-filter-popover';
import FilterMultiselect from 'components/forms/filter-multiselect';

/**
 * Reusable filter bar component matching the standardized filter UI pattern.
 *
 * Each page defines its own filter config via the `filters` prop. The bar
 * renders always-visible (essential) filters inline, plus an "+ Add Filter"
 * dropdown for optional ones. Filter state lives in the parent — this
 * component only renders UI and fires callbacks.
 *
 * Filter definition shape:
 *   {
 *     id: string,              // unique key
 *     label: string,           // display label
 *     type: 'select' | 'multiselect' | 'date' | 'text',
 *     options: array,          // for select/multiselect: [{ label, value }]
 *     value: any,              // current value — array for multiselect, object for select/date, string for text
 *     onChange: function,       // called with new value
 *     alwaysVisible: boolean,  // true = essential, false = optional
 *     alwaysShowLabel: boolean, // show uppercase label to the left (for select)
 *   }
 *
 * @param {Object} props
 * @param {Array} props.filters - filter definitions
 * @param {React.ReactNode} props.rightContent - optional content for right side of bar
 */
const FilterBar = ({ filters = [], rightContent = null }) => {
  const [activeOptionalIds, setActiveOptionalIds] = useState([]);
  const addFilterRef = useRef(null);

  const essentialFilters = filters.filter((f) => f.alwaysVisible);
  const optionalFilters = filters.filter((f) => !f.alwaysVisible);
  const visibleOptionalFilters = optionalFilters.filter((f) => activeOptionalIds.includes(f.id));
  const availableOptionalFilters = optionalFilters.filter((f) => !activeOptionalIds.includes(f.id));

  const isFilterActive = (filter) => {
    if (filter.type === 'multiselect') return (filter.value || []).length > 0;
    if (filter.type === 'select') return filter.value && filter.value.value !== 'all';
    if (filter.type === 'date') return filter.value && filter.value.operator !== 'all';
    if (filter.type === 'numeric') return filter.value && filter.value.operator !== 'all';
    if (filter.type === 'text') return !!filter.value;
    return false;
  };

  const clearFilter = (filter) => {
    if (filter.type === 'multiselect') filter.onChange([]);
    else if (filter.type === 'select') filter.onChange(filter.options?.[0] || null);
    else if (filter.type === 'date') filter.onChange({ operator: 'all', date1: null, date2: null });
    else if (filter.type === 'numeric') filter.onChange({ operator: 'all', value1: null, value2: null });
    else if (filter.type === 'text') filter.onChange('');
  };

  const removeOptionalFilter = (filterId) => {
    setActiveOptionalIds((prev) => prev.filter((id) => id !== filterId));
    const filter = optionalFilters.find((f) => f.id === filterId);
    if (filter) clearFilter(filter);
  };

  const renderFilter = (filter) => {
    if (filter.type === 'select') {
      return (
        <ListboxSelect
          size={filter.alwaysShowLabel ? 'dynamic' : 'medium'}
          options={filter.options}
          onChange={filter.onChange}
          value={filter.value}
          emptyValue={filter.label}
          label={filter.alwaysShowLabel ? `${filter.label.toUpperCase()}:` : filter.label}
          alwaysShowLabel={filter.alwaysShowLabel || false}
        />
      );
    }

    if (filter.type === 'multiselect') {
      const selectedValues = filter.value || [];
      // et-components' ListboxMultiselect locks its "initialOptionsLength" on the
      // first non-zero count. If options arrive incrementally (e.g. tags loading
      // in batches), the search box may never appear even when options exceed the
      // >10 threshold. Keying on the option count forces a remount so the
      // threshold is evaluated against the final count.
      const optCount = (filter.options || []).length;
      return (
        <FilterMultiselect
          key={filter.searchable ? `${filter.id}-${optCount}` : filter.id}
          emptyValue={filter.label}
          label={filter.label}
          options={filter.options || []}
          onChange={filter.onChange}
          values={selectedValues}
          showInfoBar={true}
        />
      );
    }

    if (filter.type === 'date') {
      return <DateFilterPopover value={filter.value} onChange={filter.onChange} label={filter.label} />;
    }

    if (filter.type === 'numeric') {
      return <NumericFilterPopover value={filter.value} onChange={filter.onChange} label={filter.label} />;
    }

    if (filter.type === 'text') {
      return (
        <div className="labeled-filter-input">
          <span className="labeled-filter-input--label">{filter.label}:</span>
          <input
            type="text"
            className="labeled-filter-input--field"
            placeholder={`Search...`}
            value={filter.value || ''}
            onChange={(e) => filter.onChange(e.target.value)}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="tasks-filters-bar">
      <div className="tasks-filters-bar--left">
        {/* Essential (always visible) filters */}
        {essentialFilters.map((filter) => (
          <div key={filter.id} className="tasks-filters-bar--core-filter">
            {renderFilter(filter)}
            {isFilterActive(filter) && (
              <button
                type="button"
                className="tasks-filters-bar--clear-filter"
                onClick={() => clearFilter(filter)}
                title={`Clear ${filter.label}`}
              >
                <Icon icon="clear" size={1} />
              </button>
            )}
          </div>
        ))}

        {/* Optional filters that have been added */}
        {visibleOptionalFilters.map((filter) => (
          <div key={filter.id} className="tasks-filters-bar--added-filter">
            {renderFilter(filter, true)}
            <button
              type="button"
              className="tasks-filters-bar--remove-filter"
              onClick={() => removeOptionalFilter(filter.id)}
              title={`Remove ${filter.label}`}
            >
              <Icon icon="clear" size={1} />
            </button>
          </div>
        ))}

        {/* + Add Filter */}
        {availableOptionalFilters.length > 0 && (
          <div ref={addFilterRef}>
            <ListboxMultiselect
              buttonClassName="add-filter-btn"
              emptyValue="+ Add Filter"
              alwaysShowLabel={true}
              size="compact"
              options={availableOptionalFilters.map((f) => ({ label: f.label, value: f.id }))}
              onChange={(vals) => {
                setActiveOptionalIds(vals.map((v) => v.value));
                // Close the dropdown after selecting a filter
                addFilterRef.current?.querySelector('.et-listbox--trigger')?.click();
              }}
              values={activeOptionalIds.map((id) => {
                const def = optionalFilters.find((f) => f.id === id);
                return { label: def?.label || id, value: id };
              })}
              label="+ Add Filter"
            />
          </div>
        )}
      </div>

      {rightContent && <div className="tasks-filters-bar--right">{rightContent}</div>}
    </div>
  );
};

FilterBar.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'multiselect', 'date', 'numeric', 'text']).isRequired,
      options: PropTypes.array,
      value: PropTypes.any,
      onChange: PropTypes.func.isRequired,
      alwaysVisible: PropTypes.bool,
      alwaysShowLabel: PropTypes.bool,
    })
  ),
  rightContent: PropTypes.node,
};

export default FilterBar;
